import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Plus, Minus, Star } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, Overline, Cta } from "@/components/site/ui";
import {
  PROCESS,
  WHY_CHOOSE,
  TESTIMONIALS,
  FAQS,
  STATS,
  SERVICES,
} from "@/data/content";

/* ---------- Inner page hero ---------- */
export const PageHero = ({ overline, title, subtitle, image }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} className="relative h-[68vh] min-h-[460px] overflow-hidden bg-[#1C1A17]">
      <motion.img
        src={image}
        alt={title}
        style={{ y }}
        className="absolute inset-0 h-[120%] w-full object-cover opacity-45"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1C1A17] via-[#1C1A17]/30 to-transparent" />
      <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col justify-end pb-16">
        <Reveal>
          <Overline dark>{overline}</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-head text-white text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter mt-5 max-w-4xl">
            {title}
          </h1>
        </Reveal>
        {subtitle && (
          <Reveal delay={0.2}>
            <p className="mt-5 max-w-xl text-white/70 text-lg leading-relaxed">{subtitle}</p>
          </Reveal>
        )}
      </div>
    </section>
  );
};

/* ---------- Editorial marquee ---------- */
export const Ribbon = () => (
  <div className="bg-[#F5F2ED] border-y border-black/5 py-6">
    <Marquee speed={38} gradient={false}>
      {["Modular Kitchens", "Wardrobes", "Full Home Interiors", "Architectural Design", "Renovation", "Smart Homes"].map(
        (t, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 font-head text-2xl sm:text-3xl font-medium text-[#1C1A17]">
            {t}
            <span className="text-[#1C1A17]/30 text-xl">✦</span>
          </span>
        )
      )}
    </Marquee>
  </div>
);

/* ---------- Stats ---------- */
export const StatsBar = ({ dark = false }) => (
  <div className={`${dark ? "bg-[#1C1A17] text-white" : "bg-white text-[#0A0A0A]"}`}>
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 py-16">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div>
            <div className="font-head text-4xl sm:text-5xl font-semibold tracking-tight">{s.value}</div>
            <div className={`mt-2 text-sm ${dark ? "text-white/50" : "text-[#5C574F]"}`}>{s.label}</div>
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

/* ---------- Why choose ---------- */
export const WhyChoose = () => (
  <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-4">
        <Reveal>
          <Overline>Why Spherio Living</Overline>
          <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5">
            A better way to build your home.
          </h2>
          <p className="mt-6 text-[#5C574F] leading-relaxed">
            We combine design taste with manufacturing precision — so the home you approve on screen
            is the home you live in.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:col-start-6 grid sm:grid-cols-2 gap-px bg-black/10 border border-black/10 rounded-2xl overflow-hidden">
        {WHY_CHOOSE.map((w, i) => (
          <Reveal key={w.no} delay={i * 0.08} className="bg-white p-8">
            <div className="font-head text-2xl font-semibold text-[#1C1A17]">{w.no}</div>
            <h3 className="mt-4 text-lg font-semibold">{w.title}</h3>
            <p className="mt-2 text-sm text-[#5C574F] leading-relaxed">{w.body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Design process ---------- */
export const ProcessSection = () => (
  <section className="bg-[#1C1A17] text-white py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
      <div className="max-w-2xl">
        <Reveal>
          <Overline dark>The Process</Overline>
          <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5">
            Five steps from idea to handover.
          </h2>
        </Reveal>
      </div>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-5 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
        {PROCESS.map((p, i) => (
          <Reveal key={p.no} delay={i * 0.08} className="bg-[#1C1A17] p-8 hover:bg-[#0F3A75] transition-colors duration-500">
            <div className="font-head text-5xl font-medium text-white/25">{p.no}</div>
            <h3 className="mt-8 text-lg font-semibold">{p.title}</h3>
            <p className="mt-2 text-sm text-white/55 leading-relaxed">{p.body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Testimonials ---------- */
export const Testimonials = () => (
  <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
    <Reveal>
      <Overline>Loved by homeowners</Overline>
      <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5 max-w-2xl">
        Stories from homes we brought to life.
      </h2>
    </Reveal>
    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
      {TESTIMONIALS.map((t, i) => (
        <Reveal key={t.name} delay={i * 0.1}>
          <figure className="h-full flex flex-col rounded-2xl border border-black/10 p-8 hover:border-[#1C1A17]/40 transition-colors duration-500">
            <div className="flex gap-1 text-[#1C1A17]">
              {[...Array(5)].map((_, k) => (
                <Star key={k} className="h-4 w-4 fill-[#1C1A17]" />
              ))}
            </div>
            <blockquote className="mt-5 text-[#0A0A0A] leading-relaxed flex-1">“{t.quote}”</blockquote>
            <figcaption className="mt-6 flex items-center gap-4">
              <img src={t.image} alt={t.name} className="h-12 w-12 rounded-full object-cover" />
              <div>
                <div className="font-semibold text-sm">{t.name}</div>
                <div className="text-xs text-[#5C574F]">{t.role}</div>
              </div>
            </figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------- FAQ ---------- */
export const FaqSection = () => (
  <section className="bg-[#F5F2ED] border-y border-black/5 py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <Reveal>
          <Overline>FAQ</Overline>
          <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5">
            Questions, answered.
          </h2>
          <p className="mt-6 text-[#5C574F]">Still curious? Our design team is one message away.</p>
          <Cta to="/contact" variant="outline" className="mt-8">
            Talk to us
          </Cta>
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:col-start-6">
        <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-black/10">
              <AccordionTrigger data-testid={`faq-${i}`} className="py-6 text-left text-lg font-medium hover:no-underline [&>svg]:hidden group">
                <span>{f.q}</span>
                <span className="ml-4 shrink-0 text-[#1C1A17]">
                  <Plus className="h-5 w-5 group-data-[state=open]:hidden" />
                  <Minus className="h-5 w-5 hidden group-data-[state=open]:block" />
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-[#5C574F] leading-relaxed text-base">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

/* ---------- Services grid (bento) ---------- */
export const ServicesGrid = () => (
  <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
      <Reveal>
        <Overline>What we do</Overline>
        <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5 max-w-xl">
          Everything your home needs, under one roof.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Cta to="/services" variant="outline">All services</Cta>
      </Reveal>
    </div>

    <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-5">
      {SERVICES.map((s, i) => (
        <Reveal
          key={s.slug}
          delay={i * 0.08}
          className={i === 0 ? "md:col-span-4" : i === 1 ? "md:col-span-2" : "md:col-span-3"}
        >
          <Link
            to={`/${s.slug}`}
            data-testid={`service-card-${s.slug}`}
            className="group block relative overflow-hidden rounded-2xl border border-black/10 h-full min-h-[300px]"
          >
            <img
              src={s.image}
              alt={s.title}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-7 text-white">
              <div className="text-white/50 font-head text-sm">{s.index}</div>
              <h3 className="font-head text-2xl font-medium mt-1">{s.title}</h3>
              <p className="mt-2 text-sm text-white/70 max-w-sm">{s.tagline}</p>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------- CTA band ---------- */
export const CtaBand = () => (
  <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24 lg:pb-32">
    <div className="grain relative overflow-hidden rounded-3xl bg-[#1C1A17] px-8 py-16 lg:p-20 text-white">
      <Reveal>
        <Overline dark>Free & no obligation</Overline>
        <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter mt-6 max-w-3xl">
          Let’s design the future of your living.
        </h2>
        <p className="mt-6 max-w-lg text-white/60 text-lg">
          Book a free consultation and get a photoreal 3D concept of your home — on us.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Cta to="/book-consultation" variant="light" data-testid="band-book-cta">
            Book Free Design Consultation
          </Cta>
          <Cta to="/portfolio" variant="ghost" data-testid="band-portfolio-cta">
            View Portfolio
          </Cta>
        </div>
      </Reveal>
    </div>
  </section>
);
